import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { DO_DB_RULE_CAP } from '@infras/shared-types';
import { EmptyState } from '../../components/EmptyState';
import { EngineIcon } from '../../components/EngineIcon';
import { Icon } from '../../components/Icon';
import { RefreshButton } from '../../components/RefreshButton';
import { engineForTarget, engineIndex, type EngineSource } from '../../lib/targetEngine';
import { relTime } from '../../lib/useFreshness';
import {
  ATLAS_CAP,
  DO_DATABASES,
  capacity,
  levelColor,
  type AtlasProjectOverview,
} from '../../lib/atlas';
import { AddTargetWizard } from './AddTargetWizard';


/**
 * The capacity half of Cluster access — one card row per access-list target, against
 * its own cap: 200 entries for an Atlas project, 100 rules for a DigitalOcean
 * database firewall (052; e-10 "show n / 100 — surface the cap like Atlas").
 *
 * Lifted verbatim out of the old `/atlas-whitelist` index page, which is now a
 * redirect. Same query, same arithmetic, same alert: it answers AC-1 Q1 ("which
 * projects are close to the cap, and how close") with no clicks, which is why it is
 * a band on the landing screen rather than a drawer.
 *
 * Nothing here calls Atlas. The counts are the snapshot written whenever the list
 * was last legitimately read, and every row states how old that reading is — see
 * `AtlasWhitelistService.listProjectOverview` for the trade.
 */

export function CapacityBand({
  projects,
  loading,
  error,
  unmappedActive,
  onRefetch,
}: {
  projects: AtlasProjectOverview[];
  loading: boolean;
  error: boolean;
  unmappedActive: number;
  /** Awaited by the sections' Refresh control — see ClusterAccessPage. */
  onRefetch: () => unknown;
}) {
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);

  // The engine join (AC-4). Unskipped, unlike the Add-target form's copy: every do_db
  // ROW needs it, not just the form. Apollo dedupes the two into one request.
  const dbs = useQuery<{ doDatabases: EngineSource[] }>(DO_DATABASES, {
    fetchPolicy: 'cache-and-network',
  });
  const engines = useMemo(() => engineIndex(dbs.data?.doDatabases), [dbs.data]);

  // AC-1: a target belongs to exactly one section, decided by `kind` and nothing else.
  // Two lists rather than one sorted list — a heading a person can scroll to is the
  // whole ask ("separate Atlas vs DigitalOcean everywhere targets are listed"), and a
  // sort would put them in the right order for as long as nobody clicked a column.
  const atlasRows = projects.filter((p) => p.kind !== 'do_db');
  const doRows = projects.filter((p) => p.kind === 'do_db');

  // The band's own refresh: the target rows AND the engine join behind their icons.
  // A refreshed row with a stale engine would be a new kind of wrong answer.
  const refreshBand = () => Promise.all([onRefetch(), dbs.refetch().catch(() => undefined)]);

  // The worst target decides whether the banner appears at all. Each kind is judged
  // against ITS OWN warn line (AC-27: a 300-rule DO row never trips the Atlas 200
  // alert — it trips the DO 100 one); ties are broken by how full it is.
  const warned = projects
    .map((p) => ({ p, cap: capacity(p.liveCount, p.pendingCount, p.kind) }))
    .filter(({ cap }) => cap.level === 'warn' || cap.level === 'crit')
    .sort((a, b) => b.cap.live / b.cap.cap - a.cap.live / a.cap.cap);
  const worst = warned[0]?.p;

  return (
    <section className="ca-band">
      <div className="detail-head">
        <h3 style={{ margin: 0 }}>Access-list targets</h3>
        <span className="badge quiet">
          {projects.length} target{projects.length === 1 ? '' : 's'}
        </span>
        <span className="spacer" />
        <Link className="btn btn-ghost" to="/cluster-access/mappings">
          Cluster mapping
          {unmappedActive > 0 && (
            <>
              {' '}
              <span className="atlas-dot" style={{ background: 'var(--amber)' }} />
              {unmappedActive} unmapped
            </>
          )}
        </Link>
        {/* Opens the wizard; it never turns into a Close, because the dialog
            carries its own Cancel and the button behind a modal is unreachable. */}
        <button type="button" className="btn btn-primary" onClick={() => setShowAdd(true)}>
          Add target
        </button>
      </div>

      <p className="detail-desc">
        Every MongoDB Atlas project caps its IP access list at {ATLAS_CAP} entries, and every
        DigitalOcean database firewall caps its trusted sources at {DO_DB_RULE_CAP} rules. Past
        that, the next node to come up never reaches the database. This band tracks what each
        target is using, what is waiting to be pushed, and how much room is left.
      </p>

      <AddTargetWizard
        open={showAdd}
        onClose={() => setShowAdd(false)}
        existingKeys={projects.map((p) => p.projectKey)}
        onDone={(key) => {
          setShowAdd(false);
          onRefetch();
          navigate(`/cluster-access/project/${encodeURIComponent(key)}`);
        }}
      />

      {error && (
        <>
          <p className="error">Couldn&apos;t load the access-list targets — the API didn&apos;t respond.</p>
          <p className="muted">
            Nothing was changed in Atlas; this page only reads. If it persists, ask in
            #devops-infra.
          </p>
        </>
      )}

      {/* F-quality-4 (review e-29): the engine join has its own query, and its failure
          used to render as a row of "unknown engine" icons that read like a data fact.
          Say the join failed instead — the target rows themselves are still good. */}
      {!error && dbs.error && (
        <p className="error" role="alert">
          Couldn&apos;t load the DigitalOcean database list — engine icons below may read as
          unknown. The targets themselves are current.
        </p>
      )}

      {worst && <CapAlert project={worst} />}

      {/* F-1 (review e-11): when the load FAILED there are no rows to describe, so
          the two "No … yet — add one" lines below would be a claim this screen
          cannot make — it does not know whether targets exist. The error paragraphs
          above are the whole answer. */}
      {error && !projects.length ? null : loading && !projects.length ? (
        <div className="skeleton-row-group">
          {[0, 1, 2].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      ) : !projects.length && !error ? (
        <EmptyState
          icon="shield"
          title="No access-list targets yet"
          body="Add one with Add target above — an Atlas project (project id + OAuth client id and secret) or a DigitalOcean database firewall (a scanned database + API token). The secret is encrypted before it is stored, never kept in plaintext."
        />
      ) : (
        <>
          {/* Both sections always render, even empty (AC-2). A heading that disappears
              with its last row makes the page look like the old single mixed list
              again, and hides where a new target of that kind would land. */}
          <TargetSection
            title="MongoDB Atlas projects"
            rows={atlasRows}
            engines={engines}
            emptyText="No Atlas projects yet — add one with Add target above."
            onRefetch={refreshBand}
          />
          <TargetSection
            title="DigitalOcean databases"
            rows={doRows}
            engines={engines}
            emptyText="No DigitalOcean database targets yet — add one with Add target above."
            onRefetch={refreshBand}
          />
        </>
      )}
    </section>
  );
}

/** One kind's heading, count, refresh and table. Two instances, one rule. */
function TargetSection({
  title,
  rows,
  engines,
  emptyText,
  onRefetch,
}: {
  title: string;
  rows: AtlasProjectOverview[];
  engines: Map<string, EngineSource>;
  emptyText: string;
  onRefetch: () => unknown;
}) {
  return (
    <section className="ca-section" style={{ marginTop: '1rem' }}>
      <div className="detail-head">
        <h4 style={{ margin: 0 }}>{title}</h4>
        <span className="badge quiet">
          {rows.length} target{rows.length === 1 ? '' : 's'}
        </span>
        <span className="spacer" />
        <RefreshButton refetch={onRefetch} />
      </div>
      {!rows.length ? (
        <p className="muted text-xs" style={{ margin: '0.25rem 0 0' }}>
          {emptyText}
        </p>
      ) : (
        <div className="atlas-card">
          <table className="atlas-table">
            <thead>
              <tr>
                <th>Target</th>
                <th>Access list usage</th>
                <th>Waiting to push</th>
                <th>Clusters</th>
                {/* REMOVED 2026-08-25: an "Agents reporting · alert > 30 min" column,
                    with its own band from `agentsStale`. It was a third clock a human
                    could see — one cluster could read "3 silent" here and a different
                    verdict on the fleet list at the same moment, so the column needed a
                    title explaining that it was not the rule that decided anything. Who
                    has stopped reporting is the fleet list's question and the Overview
                    checklist's line, both on the one clock. Nothing was lost but a
                    second answer. */}
                <th>Last sync</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <ProjectRow key={p.projectKey} p={p} engines={engines} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Shown only at or past the warn line. Red past crit, because the difference
 *  between "clean up soon" and "the next push fails" is the whole message. */
function CapAlert({ project }: { project: AtlasProjectOverview }) {
  const cap = capacity(project.liveCount, project.pendingCount, project.kind);
  const crit = cap.rejected || cap.level === 'crit';
  const unit = cap.kind === 'do_db' ? 'rules' : 'entries';
  const vendor = cap.kind === 'do_db' ? 'DigitalOcean' : 'Atlas';
  return (
    <div className={`atlas-alert${crit ? ' atlas-alert--crit' : ''}`}>
      <Icon name="alert" size={17} />
      <div className="atlas-alert__body">
        <div className="atlas-alert__title">
          {project.displayName || project.projectKey} is at {cap.live} of {cap.cap} {unit} —{' '}
          {cap.slotsLeft} slot{cap.slotsLeft === 1 ? '' : 's'} left
        </div>
        <div className="atlas-alert__text">
          {cap.pending > 0 ? (
            <>
              {cap.pending} reported IP{cap.pending === 1 ? ' is' : 's are'} still waiting to be
              pushed, which would take it to {cap.projected}.{' '}
              {cap.rejected
                ? `That is past the ${cap.cap} limit, so the next reconcile will be rejected outright — new nodes will not reach the database.`
                : `Alerts start at ${cap.warn} so there is room to clean up before ${vendor} starts rejecting adds.`}
            </>
          ) : (
            <>
              Nothing is waiting to be pushed right now, but only {cap.slotsLeft} slot
              {cap.slotsLeft === 1 ? '' : 's'} remain for the next nodes to come up.
            </>
          )}
        </div>
      </div>
      <Link className="btn" to={`/cluster-access/project/${encodeURIComponent(project.projectKey)}`}>
        Review target
      </Link>
    </div>
  );
}

function ProjectRow({
  p,
  engines,
}: {
  p: AtlasProjectOverview;
  engines: Map<string, EngineSource>;
}) {
  const cap = capacity(p.liveCount, p.pendingCount, p.kind);
  const color = levelColor(cap.level);
  const engine = engineForTarget(p, engines);

  return (
    <tr>
      <td>
        {/* The engine icon replaces the "DO firewall" badge this row used to carry.
            The badge said the KIND, which the section heading above now says once for
            every row in it; what a reader could NOT tell was which database — three
            DigitalOcean rows read identically in text whether they front a Postgres, a
            Valkey or a MySQL firewall. The icon is the only statement of that, so it
            carries the name rather than being decorative (AC-4). */}
        <span className="atlas-proj-name">
          <EngineIcon engine={engine} />
          <Link
            className="atlas-proj"
            to={`/cluster-access/project/${encodeURIComponent(p.projectKey)}`}
          >
            {p.displayName || p.projectKey}
            {!p.isActive && <span className="muted"> (inactive)</span>}
          </Link>
        </span>
        <div className="atlas-sub mono">{p.projectKey}</div>
      </td>

      <td>
        <div className="atlas-usage">
          {/* A project with no credentials has UNKNOWN usage. A zero-width bar over a
              dim track says that; an empty bar over a normal track would claim it is
              using none of its slots. */}
          <span className="atlas-bar">
            <span
              className="atlas-bar__live"
              style={{ width: `${cap.livePct}%`, background: cap.known ? color : 'transparent' }}
            />
            <span className="atlas-bar__pending" style={{ width: `${cap.pendingPct}%` }} />
          </span>
          <span
            className="atlas-usage__n mono"
            style={{ color: cap.level === 'ok' ? undefined : color }}
          >
            {cap.known ? `${cap.live} / ${cap.cap}` : '—'}
          </span>
        </div>
        <div className="atlas-sub">
          {!cap.known
            ? 'no credentials — list never read'
            : cap.level !== 'ok'
              ? `${cap.slotsLeft} slot${cap.slotsLeft === 1 ? '' : 's'} left`
              : p.liveCountAt
                ? `read ${relTime(Date.now() - new Date(p.liveCountAt).getTime())} ago`
                : 'healthy'}
        </div>
      </td>

      <td
        className="mono"
        style={{ color: cap.pending && cap.level !== 'ok' ? 'var(--amber)' : undefined }}
      >
        {cap.pending ? `+${cap.pending}` : '—'}
      </td>

      <td>{p.clusterCount}</td>

      <td className="muted">
        <span
          className="atlas-dot"
          style={{
            background:
              p.lastSyncStatus === 'ok'
                ? 'var(--green)'
                : p.lastSyncAt
                  ? 'var(--amber)'
                  : 'var(--faint-deep)',
          }}
        />
        {p.lastSyncAt ? `${relTime(Date.now() - new Date(p.lastSyncAt).getTime())} ago` : 'never'}
      </td>
    </tr>
  );
}
